// MO4.1.2
import React, { useCallback, useState, useRef, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import Flex from '@wix/da-ds/pkg/Flex';
import { Url } from '@wix/da-url';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import CheckIcon from '@wix/da-ds/pkg/Icons/16x16/Check';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import Avatar from '../../User/Avatar';
import { getDeviationEntityId } from '../../redux/normalizr/schemas/deviation';
import BasicUserName from '../../User/BasicUserName';
import SubscriberStat from '../../Stats/SubscriberStat';
import TierPriceWithBadge from '../Helper/TierPriceWithBadge';
import MinimalDumbo from '../Dumbo/MinimalDumbo';
import SubscribeButton from '../../Button/SubscribeButton';
import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton';
import { getTierDescription } from '../_helpers';
import { useMeasuredCookie } from '../../Measured/useMeasuredCookie';
import { useDeviationMatureFilter } from '../../utils/hooks/useDeviationMatureFilter';
import { useIsCurrentUserDeviationAuthor } from '../../utils/hooks/useIsCurrentUserDeviationAuthor';
import HiddenOverlay from '../HiddenOverlay';
import s from './TierSidebarCard.scss';
const TierSidebarCard = ({ deviation, biData, className, showRecommendedBadge = true, editTier })=>{
    var _author, _measuredCoverWrapper;
    const { t } = useTranslation();
    const { deviationId, author, title } = deviation;
    const { dollarPrice, isUserSubscribed } = deviation.tier || {};
    const isOwner = useIsCurrentUserDeviationAuthor(deviation);
    const updated_tier_sidebar_card = useFlag('updated_tier_sidebar_card');
    const isMobile = useContext(MobileContext);
    const coverWrapperRef = useRef(null);
    const measuredCoverWrapper = useMeasuredCookie(undefined, coverWrapperRef, {
        cookieDim: 'width'
    });
    const { showBlocked, overrideMatureFilter } = useDeviationMatureFilter(deviation);
    const [matureFilterOverridden, setMatureFilterOverridden] = useState(false);
    const onOverrideFilterClick = useCallback((e)=>{
        overrideMatureFilter();
        setMatureFilterOverridden(true);
        e.stopPropagation();
        e.preventDefault();
    }, [
        overrideMatureFilter,
        setMatureFilterOverridden
    ]);
    if (!((_author = author) === null || _author === void 0 ? void 0 : _author.username) || !dollarPrice) {
        return null;
    }
    const tierEntityId = getDeviationEntityId(deviation);
    const coverWidth = updated_tier_sidebar_card && isMobile ? (_measuredCoverWrapper = measuredCoverWrapper) === null || _measuredCoverWrapper === void 0 ? void 0 : _measuredCoverWrapper.width : 274;
    const coverHeight = updated_tier_sidebar_card ? 100 : 152;
    const href = isUserSubscribed ? Url.userSubscriptionsPath({
        username: author.username,
        tierDeviationId: deviationId,
        tierContentType: 'deviations',
        anchor: true
    }) : Url.userSubscriptionsDeeplink({
        username: author.username,
        tierEntityId
    });
    const description = getTierDescription(deviation);
    return /*#__PURE__*/ React.createElement(PlainButtonOrLink, {
        className: classnames(s['root'], updated_tier_sidebar_card && s['updated_tier_sidebar_card'], className),
        biData: biData,
        href: href
    }, showBlocked && /*#__PURE__*/ React.createElement(HiddenOverlay, {
        className: s['card-overlay'],
        showBackground: false,
        deviation: deviation,
        overrideMatureFilter: onOverrideFilterClick
    }), /*#__PURE__*/ React.createElement("div", {
        className: s['top-section']
    }, /*#__PURE__*/ React.createElement("div", {
        className: s['user-info']
    }, /*#__PURE__*/ React.createElement(Avatar, {
        user: author,
        size: 24,
        withLink: false
    }), /*#__PURE__*/ React.createElement(BasicUserName, {
        usernameClassName: s['username'],
        symbolClassName: s['user-symbol'],
        user: author
    }), !updated_tier_sidebar_card && isUserSubscribed && /*#__PURE__*/ React.createElement("span", {
        className: s['subscribed']
    }, /*#__PURE__*/ React.createElement(CheckIcon, {
        className: s['subscribed-icon'],
        size: IconSize.SMALLER
    }), ' ', t('deviation.tier.subscribed'))), updated_tier_sidebar_card && showRecommendedBadge && /*#__PURE__*/ React.createElement("div", {
        className: s['recommended-badge']
    }, t('deviation.tier.recommended'))), /*#__PURE__*/ React.createElement("div", {
        className: s['cover-wrapper'],
        ref: coverWrapperRef
    }, deviation.coverImage ? /*#__PURE__*/ React.createElement(MinimalDumbo, {
        className: s['cover'],
        deviation: deviation.coverImage,
        height: coverHeight,
        width: coverWidth,
        showMatureContent: showBlocked || matureFilterOverridden
    }) : /*#__PURE__*/ React.createElement("div", {
        className: classnames(s['cover'], s['placeholder-cover']),
        style: {
            height: coverHeight
        }
    }), updated_tier_sidebar_card && /*#__PURE__*/ React.createElement(SubscriberStat, {
        className: s['subscribers'],
        deviation: deviation
    })), title && /*#__PURE__*/ React.createElement(Heading, {
        className: s['title']
    }, title), !updated_tier_sidebar_card && /*#__PURE__*/ React.createElement(SubscriberStat, {
        className: s['subscribers'],
        deviation: deviation
    }), description && /*#__PURE__*/ React.createElement("div", {
        className: s['description']
    }, description), /*#__PURE__*/ React.createElement(Flex, {
        alignItems: "center",
        justifyContent: updated_tier_sidebar_card ? 'space-between' : undefined
    }, /*#__PURE__*/ React.createElement(TierPriceWithBadge, {
        className: s['price'],
        price: dollarPrice
    }), updated_tier_sidebar_card && (isOwner ? /*#__PURE__*/ React.createElement(GhostButton, {
        size: "medium",
        variant: "edit-purple",
        onClick: editTier
    }, t('common.edit')) : /*#__PURE__*/ React.createElement(SubscribeButton, {
        tier: deviation,
        showUnsubAsView: true
    }))));
};
TierSidebarCard.displayName = 'TierSidebarCard';
export default TierSidebarCard;
